<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { ref, inject, onMounted, watch } from 'vue';
import ConsigneeForm from '@/components/Customer/ConsigneeForm.vue';
import { addressListTemplate } from '@/config/templates/addressListTemplate';
import useAlertStore from '@/stores/alert';
import BaseSearch from '@/components/widgets/BaseSearchDialog.vue';
import { useInfinite } from '@/composables/use-infinity';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });
const backendApi = inject('backendApi');
const alertStore = useAlertStore();

const emit = defineEmits([
    'saveConsignee',
    'update:focused',
    'update:modelValue',
    'update:args'
]);

const props = defineProps({
    fieldModels: { default: {} },
    hideLabel: { type: Boolean, default: false },
    density: { type: String, default: '' },
    hideDetails: { default: '' },
    rows: { default: '' },
    errorMessages: { default: '' },
    variant: { default: '' },
    readonly: { default: '' },
    color: { default: '' },
    class: { default: '' },
    is: { default: '' },
    returnObject: { default: '' },
    modelValue: { default: '' },
    clearable: { default: true },
    label: String,
    multiple: Boolean,
    disabled: Boolean,
    documentType: String,
    args: {
        type: Object,
        default: () => ({})
    }
});

const selected = ref(null);
const ConsigneeFormRef = ref(null);
const showAddressList = ref(false);

const getItemTitle = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return (
        item.fullName ||
        item.number ||
        item.adrdefault?.fullName ||
        item.adrdefault?.number ||
        item.address ||
        (item.adrdefault
            ? item.adrdefault.fullName ||
              item.adrdefault.number ||
              item.adrdefault.address
            : '') ||
        ''
    );
};

const fetchAddresses = ({ pattern, page, limit }) =>
    backendApi()
        .customers()
        .searchAddress({
            pattern,
            page,
            limit,
            customerId: props.args?.customer?.id
        })
        .then((res) => res);

const {
    items: customers,
    loading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchAddresses);

const onCustomerPressed = () => {
    const shipper = props.args.shipper;
    if (shipper) {
        ConsigneeFormRef.value.open(null, shipper);
    } else {
        alertStore.addMessage(
            t('You should select a shipper before create a consignee')
        );
    }
};

const openAddressList = () => {
    if (props.disabled) return;
    if (!props.args?.shipper) {
        alertStore.addMessage(
            t('You have to select a shipper before open the address list')
        );
        return;
    }
    showAddressList.value = true;
};

const saveConsignee = async (consignee) => {
    emit('saveConsignee', consignee);
    emit('update:modelValue', consignee);
};

const loadAddressList = async () => {
    return await backendApi().customers().searchAddress({
        page: 1,
        limit: 1000,
        customerId: props.args.shipper.id
    });
};

const onClearPressed = () => {
    searchQuery.value = '';
    selected.value = null;
    emit('update', selected.value);
    reset();
    getItems();
};

const previousCustomerId = ref(null);
watch(
    () => props.args?.customer?.id,
    (newCustomerId) => {
        if (newCustomerId !== previousCustomerId.value) {
            previousCustomerId.value = newCustomerId;
            selected.value = null;
            emit('update', null);
            reset();
            getItems();
        }
    },
    { immediate: true }
);

watch(customers, (newCustomers) => {
    emit('update:args', { addresses: newCustomers });
});

const handleAddress = (address) => {
    if (address[0]) {
        selected.value = address[0];
        emit('update:modelValue', address[0]);
        emit('update', address[0]);
    }
};

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue && typeof newValue === 'object') {
            selected.value = newValue;
        } else if (!newValue) {
            selected.value = null;
        }
    },
    { immediate: true, deep: true }
);

onMounted(() => getItems());
</script>

<template>
    <AutocompleteFilter
        @update:modelValue="(val) => emit('update:modelValue', val)"
        :modelValue="modelValue"
        v-model:search="searchQuery"
        :items="customers"
        :is="is"
        :hide-details="hideDetails"
        :density="density"
        :rows="rows"
        :error-messages="errorMessages"
        :variant="variant"
        :readonly="readonly || disabled"
        :disabled="disabled"
        :color="color"
        :return-object="returnObject"
        :item-title="getItemTitle"
        item-value="id"
        :label="hideLabel ? '' : $t('Customer Address')"
        :loading="loading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        :showOptionsButton="!disabled"
        @open-dialog="openAddressList"
        @infinite-scroll="loadMore"
        @update="emit('update', selected)"
        @preparing-search="
            () => {
                reset();
                getItems();
            }
        "
        @search="
            () => {
                reset();
                getItems();
            }
        "
        @retry="
            () => {
                reset();
                getItems();
            }
        "
        @click:clear="onClearPressed"
        @update:focused="(state) => emit('update:focused', state)"
        :clearable="clearable"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="item.raw.fullName"
                :subtitle="item.raw.adrdefault?.city?.longFormat"
            ></v-list-item>
        </template>
        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
        <template v-slot:prepend-item>
            <v-btn
                color="primary"
                variant="tonal"
                class="btnNewCustomer"
                prepend-icon="mdi-account-plus-outline"
                @click="onCustomerPressed"
            >
                {{ $t('Create new') }}
            </v-btn>
        </template>
        <template v-slot:selection="{ item }">
            <span v-if="item && item.raw">
                {{
                    item.raw.fullName ||
                    item.raw.number ||
                    item.raw.adrdefault?.fullName ||
                    item.raw.adrdefault?.number ||
                    item.raw.address ||
                    ''
                }}
            </span>
            <span v-else-if="modelValue && typeof modelValue === 'object'">
                {{
                    modelValue.fullName ||
                    modelValue.number ||
                    modelValue.adrdefault?.fullName ||
                    modelValue.adrdefault?.number ||
                    modelValue.address ||
                    (modelValue.adrdefault
                        ? modelValue.adrdefault.fullName ||
                          modelValue.adrdefault.number ||
                          modelValue.adrdefault.address
                        : '') ||
                    ''
                }}
            </span>
        </template>
    </AutocompleteFilter>
    <ConsigneeForm
        ref="ConsigneeFormRef"
        :title-page="$t('Packages')"
        @update="saveConsignee"
        @saved="saveConsignee"
    ></ConsigneeForm>

    <BaseSearch
        v-model="showAddressList"
        :title="$t('Addres List')"
        :columns="addressListTemplate[0].fields"
        :loadData="loadAddressList"
        :canExtractItems="true"
        :multiple="false"
        @update:model-value="() => (showAddressList = false)"
        @selected="handleAddress"
    />
</template>

<style lang="scss" scoped>
.btnNewCustomer {
    margin-left: 12px;
    margin-right: auto;
}
</style>
